import React from "react";
import { Box, Paper, Typography, IconButton, Stack, Tooltip } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";

// ============================================================================
// Types
// ============================================================================

interface SectionCardProps {
  title: string;
  // Content to display (truncated to maxPreviewLength)
  content?: string;
  // Shown when content is absent
  placeholder?: string;
  maxPreviewLength?: number;
  // 4px left accent border color — use theme palette values or hex
  accentColor?: string;
  // When provided, renders an edit icon button
  onEdit?: () => void;
  // When provided, renders a microphone next to the pencil: the same editor, opened with the
  // recorder already running. Absent on a browser with no microphone, so the icon is never a
  // control that cannot do anything.
  onDictate?: () => void;
}

// ============================================================================
// Component
// ============================================================================

const SectionCard: React.FC<SectionCardProps> = ({
  title,
  content = "",
  placeholder = "Not documented",
  maxPreviewLength = 600,
  accentColor,
  onEdit,
  onDictate,
}) => {
  const preview =
    content.length > maxPreviewLength
      ? content.substring(0, maxPreviewLength) + "…"
      : content;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 2,
        position: "relative",
        ...(accentColor && { borderLeft: `4px solid ${accentColor}` }),
      }}
    >
      {/* Title row */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: accentColor ?? "text.primary",
          }}
        >
          {title}
        </Typography>

        {/* Dictate sits before the pencil deliberately: on a clinical note the quick action is
            "add what I just found", and typing is the fallback rather than the other way round.
            Both open the same editor, so this is one destination with two ways in. */}
        <Stack direction="row" spacing={0.5} sx={{ ml: 1, flexShrink: 0 }}>
          {onDictate && (
            <Tooltip title="Dictate">
              <IconButton size="small" onClick={onDictate} sx={{ color: "text.secondary" }}>
                <MicNoneOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onEdit && (
            <Tooltip title="Edit">
              <IconButton size="small" onClick={onEdit} sx={{ color: "text.secondary" }}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>

      {/* Content preview */}
      <Typography
        variant="body2"
        sx={{
          color: content ? "text.primary" : "text.disabled",
          whiteSpace: "pre-wrap",
        }}
      >
        {preview || placeholder}
      </Typography>
    </Paper>
  );
};

export default SectionCard;
